import { Add, Close, Delete } from "@mui/icons-material";
import {
    Autocomplete,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import axios from "axios";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchCustomers } from "../../../../../api/customers";
import { bulkLinkCustomers, fetchProfiles } from "../../../../../api/profile";
import { useCurrentProfile } from "../../../../../ContextWrapper";
import {
    getAllowedAccountRoles,
    getExistingRoleHolder,
    getExternalUserTypesDisplay,
    getInternalUserTypesDisplay,
    INCOMPLETE_ROLE_LABEL,
    isRoleAllowedForUserType,
    mapRoleLabelToBackend,
    mapUserTypeDisplayToBackend,
    normalizeAccountRoleLabel
} from "../../../../../utils/userRoleUtils";

export default function UserCreateDialog({ onClose, onSubmit, onShowSuccess, onShowError }) {
  const navigate = useNavigate();
  const { profile } = useCurrentProfile();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    userCategory: "",
    userType: "",
    linkedAccounts: []
  });

  const [emailError, setEmailError] = useState("");
  const [newAccount, setNewAccount] = useState({
    accountName: "",
    userType: "",
    customerId: null,
    bookkeeper: null,
    manager: null,
  });
  

  const [customers, setCustomers] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [linkedAccountsOpen, setLinkedAccountsOpen] = useState(false);
  const [emailCheckTimeout, setEmailCheckTimeout] = useState(null);

  const internalUserTypes = useMemo(() => getInternalUserTypesDisplay(), []);
  const externalUserTypes = useMemo(() => getExternalUserTypesDisplay(), []);

  const userCategoryOptions = ["Internal", "External"];

  const getUserTypeOptions = useCallback((category) => {
    if (category === "Internal") {
      return internalUserTypes.map((type) => ({ value: type, label: type }));
    } else if (category === "External") {
      return externalUserTypes.map((type) => ({ value: type, label: type }));
    }
    return [];
  }, [internalUserTypes, externalUserTypes]);

  const userTypeOptions = useMemo(
    () => getUserTypeOptions(formData.userCategory),
    [formData.userCategory, getUserTypeOptions]
  );

  const allowedAccountRoles = useMemo(() => (
    formData.userType ? getAllowedAccountRoles(formData.userType) : []
  ), [formData.userType]);

  const alignRoleToUserType = useCallback((role, userType) => {
    if (!role || role === INCOMPLETE_ROLE_LABEL) {
      return INCOMPLETE_ROLE_LABEL;
    }

    const normalized = normalizeAccountRoleLabel(role);
    if (!normalized) {
      return INCOMPLETE_ROLE_LABEL;
    }

    if (!userType) {
      return normalized;
    }

    return isRoleAllowedForUserType(normalized, userType)
      ? normalized
      : INCOMPLETE_ROLE_LABEL;
  }, []);

  useEffect(() => {
    setNewAccount((prev) => {
      const nextRole =
        prev.userType && allowedAccountRoles.includes(prev.userType)
          ? prev.userType
          : "";

      if (prev.userType === nextRole) {
        return prev;
      }

      return {
        ...prev,
        userType: nextRole,
      };
    });
  }, [allowedAccountRoles]);

  const updateLinkedAccountRole = useCallback(
    (accountId, role) => {
      setFormData((prev) => {
        const updatedLinkedAccounts = prev.linkedAccounts.map((account) => {
          if (account.id !== accountId) {
            return account;
          }

          const alignedRole = alignRoleToUserType(role, prev.userType);
          return {
            ...account,
            userType: alignedRole,
          };
        });

        return {
          ...prev,
          linkedAccounts: updatedLinkedAccounts,
        };
      });
    },
    [alignRoleToUserType]
  );

  const handleLinkedAccountRoleChange = useCallback(
    (accountId, role) => {
      if (!role) {
        updateLinkedAccountRole(accountId, "");
        return;
      }

      if (!allowedAccountRoles.includes(role)) {
        return;
      }

      updateLinkedAccountRole(accountId, role);
    },
    [allowedAccountRoles, updateLinkedAccountRole]
  );

  // Check if email exists in the database (debounced)
  const checkEmailExists = async (email) => {
    if (!email || email.length < 4) {
      setEmailError("");
      return;
    }

    try {
      const response = await fetchProfiles({ search: email, page_size: 1 });
      const matchingUser = response.results?.find(
        profile => profile.user?.email?.toLowerCase() === email.toLowerCase()
      );
      
      if (matchingUser) {
        setEmailError("This email address is already registered in the system");
      } else {
        setEmailError("");
      }
    } catch (error) {
      // If the API fails, silently continue (user can still try to submit)
      setEmailError("");
    }
  };

  const handleEmailChange = (event) => {
    const email = event.target.value;
    setFormData((prev) => ({ ...prev, email }));

    if (emailCheckTimeout) {
      clearTimeout(emailCheckTimeout);
      setEmailCheckTimeout(null);
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (email && !emailRegex.test(email)) {
      setEmailError("Please enter a valid email address");
      return;
    }

    if (email && email.length >= 4) {
      const timeout = setTimeout(() => {
        checkEmailExists(email);
      }, 500);
      setEmailCheckTimeout(timeout);
    } else {
      setEmailError("");
    }
  };

  const fetchCustomersList = async (searchTerm = "") => {
    if (!searchTerm || searchTerm.length < 2) {
      setCustomers([]);
      setLoadingCustomers(false);
      return;
    }
    
    setLoadingCustomers(true);
    try {
      // If we have a search term, use server-side search with pagination
      if (searchTerm && searchTerm.trim()) {
        const params = {
          search: searchTerm.trim(),
          page_size: 10
        };
        
        const response = await fetchCustomers(profile, params);
        const results = (response.results || []).map(customer => ({
          ...customer,
          optionType: "account"
        }));

        if (results.length === 0) {
          setCustomers([
            {
              id: "__no_account_results__",
              optionType: "noResults",
              searchTerm
            }
          ]);
        } else {
          setCustomers(results);
        }
      } else {
        let allCustomers = [];
        let nextCursor = null;
        let pageCount = 0;
        const maxPages = 5;
        
        do {
          pageCount++;
          
          const params = { page_size: 10 };
          if (nextCursor) {
            params.cursor = nextCursor;
          }
          
          const response = await fetchCustomers(profile, params);
          allCustomers = [
            ...allCustomers,
            ...((response.results || []).map(customer => ({
              ...customer,
              optionType: "account"
            })))
          ];
          nextCursor = response.next ? new URL(response.next).searchParams.get('cursor') : null;
          
          // Break if we've reached max pages or no more results
          if (pageCount >= maxPages || !nextCursor) {
            break;
          }
        } while (nextCursor);

        if (allCustomers.length === 0) {
          setCustomers([
            {
              id: "__no_account_results__",
              optionType: "noResults",
              searchTerm
            }
          ]);
        } else {
          setCustomers(allCustomers);
        }
      }
    } catch (error) {
      setCustomers([
        {
          id: "__no_account_results__",
          optionType: "noResults",
          searchTerm
        }
      ]);
    } finally {
      setLoadingCustomers(false);
    }
  };

  const handleChange = (field) => (event) => {
    const value = event.target.value;

    if (field === "userCategory") {
      setFormData((prev) => ({
        ...prev,
        userCategory: value,
        userType: "",
        linkedAccounts: prev.linkedAccounts.map((account) => ({
          ...account,
          userType: INCOMPLETE_ROLE_LABEL,
        })),
      }));

      setNewAccount({
        accountName: "",
        userType: "",
        customerId: null,
        bookkeeper: null,
        manager: null,
      });

      return;
    }

    if (field === "userType") {
      const allowedForValue = getAllowedAccountRoles(value);

      setFormData((prev) => {
        const updatedLinkedAccounts = prev.linkedAccounts.map((account) => {
          const alignedRole = alignRoleToUserType(account.userType, value);
          return {
          ...account,
            userType: alignedRole,
          };
        });

        return {
          ...prev,
          userType: value,
          linkedAccounts: updatedLinkedAccounts,
        };
      });

      setNewAccount((prev) => ({
          ...prev,
        userType: prev.userType && allowedForValue.includes(prev.userType) ? prev.userType : "",
      }));

      return;
    }

    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddAccount = () => {
    if (!newAccount.accountName || !newAccount.userType) {
      return;
    }

    if (!allowedAccountRoles.includes(newAccount.userType)) {
      return;
    }

    const trimmedAccountName = newAccount.accountName.trim();
    if (!trimmedAccountName) {
      return;
    }

    const isAlreadyLinked = formData.linkedAccounts.some((account) => {
      const existingId = account.customerId || account.id;
      if (existingId && newAccount.customerId && existingId === newAccount.customerId) {
        return true;
      }
      return account.accountName === trimmedAccountName;
    });
      
      if (isAlreadyLinked) {
        return;
      }
      
    setFormData((prev) => ({
          ...prev,
      linkedAccounts: [
        ...prev.linkedAccounts,
        {
          id: newAccount.customerId || Date.now(),
          customerId: newAccount.customerId,
          accountName: trimmedAccountName,
          userType: newAccount.userType,
          bookkeeper: newAccount.bookkeeper,
          manager: newAccount.manager,
        },
      ],
    }));

    setNewAccount({
      accountName: "",
      userType: "",
      customerId: null,
      bookkeeper: null,
      manager: null,
    });
  };

  const handleRemoveAccount = (accountId) => {
    setFormData(prev => ({
      ...prev,
      linkedAccounts: prev.linkedAccounts.filter(account => account.id !== accountId)
    }));
  };

  const handleCreateNewAccount = () => {
    onClose();
    navigate('/management/accounts');
    setTimeout(() => {
      const event = new CustomEvent('openAccountCreateDialog');
      window.dispatchEvent(event);
    }, 100);
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.email || !formData.userType || emailError) {
      return;
    }

    try {
      const nameParts = formData.name.trim().split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      const backendUserType = mapUserTypeDisplayToBackend(formData.userType);
      if (!backendUserType) {
        return;
      }

      const profileData = {
        email: formData.email,
        type: backendUserType,
        first_name: firstName,
        last_name: lastName,
        legacy_user: false,
      };

      const response = await axios.post('/api/v2/profiles/', profileData, {
        headers: { "Content-Type": "application/json" },
        withCredentials: true,
      });

      const newProfile = response.data;

      if (formData.linkedAccounts.length > 0) {
        const accountRoles = {};
        const customersToLinkSet = new Set();
      const allowedRolesForSubmit = getAllowedAccountRoles(formData.userType);

        formData.linkedAccounts.forEach(account => {
          const customerId = account.customerId || account.id;
          if (!customerId) {
            return;
          }

        const alignedRole = alignRoleToUserType(account.userType, formData.userType);
          if (
          alignedRole === INCOMPLETE_ROLE_LABEL ||
          !allowedRolesForSubmit.includes(alignedRole)
          ) {
            return;
          }

          customersToLinkSet.add(customerId);
        accountRoles[customerId] = mapRoleLabelToBackend(alignedRole);
        });

        const customersToLink = Array.from(customersToLinkSet);

        if (customersToLink.length > 0) {
        try {
          await bulkLinkCustomers(newProfile.id, customersToLink, [], accountRoles);
        } catch (linkError) {
          console.error('Error linking accounts:', linkError);
          }
        }
      }

      onSubmit?.(newProfile);
      
      // Show success message before closing dialog
      if (onShowSuccess) {
        onShowSuccess("User created successfully!");
      }
      
      onClose();
    } catch (error) {
      console.error('Error creating user:', error);
      console.error('Error response:', error.response?.data);
    }
  };

  // Memoized form validation - no password validation needed since we always send invites
  const isFormValid = useMemo(() => {
    return formData.name && formData.email && formData.userCategory && formData.userType && !emailError;
  }, [formData.name, formData.email, formData.userCategory, formData.userType, emailError]);

  return (
    <Dialog 
      open={true} 
      onClose={onClose}
      maxWidth="md" 
      fullWidth
      sx={{ zIndex: 99999 }}
      PaperProps={{
        sx: { borderRadius: 2, minHeight: "600px" }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h6" sx={{ fontWeight: "bold" }}>
            Create User
          </Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Here you can view user details, track connections, and edit essentials.
        </Typography>
      </DialogTitle>
      
      <Divider />
      
      <DialogContent sx={{ pt: 3 }}>
        <Stack spacing={3}>
          {/* Primary Fields */}
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              Primary Fields
            </Typography>
            <Grid container rowSpacing={3} columnSpacing={2}>
              <Grid size={6}>
                <TextField
                  label="Name"
                  value={formData.name}
                  onChange={handleChange("name")}
                  required
                  fullWidth
                  size="small"
                  placeholder="Enter full name"
                />
              </Grid>
              <Grid size={6}>
                <TextField
                  label="Email Address"
                  type="email"
                  value={formData.email}
                  onChange={handleEmailChange}
                  required
                  fullWidth
                  size="small"
                  error={!!emailError}
                  helperText={emailError}
                  placeholder="Enter email address"
                />
              </Grid>
              
              <Grid size={6}>
                <FormControl required fullWidth size="small">
                  <InputLabel>User Category</InputLabel>
                  <Select
                    value={formData.userCategory}
                    onChange={handleChange("userCategory")}
                    label="User Category"
                    MenuProps={{ sx: { zIndex: 99999 } }}
                    renderValue={(selected) => {
                      return <span style={{ fontSize: '0.875rem' }}>{selected}</span>;
                    }}
                    sx={{
                      '& .MuiSelect-select': {
                        fontSize: '0.875rem'
                      }
                    }}
                  >
                    {userCategoryOptions.map((category) => (
                      <MenuItem key={category} value={category}>
                        {category}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={6}>
                <FormControl required fullWidth size="small">
                  <InputLabel>Primary User Type</InputLabel>
                  <Select
                    value={formData.userType}
                    onChange={handleChange("userType")}
                    label="Primary User Type"
                    disabled={!formData.userCategory}
                    MenuProps={{ sx: { zIndex: 99999 } }}
                    renderValue={(selected) => {
                      return <span style={{ fontSize: '0.875rem' }}>{selected}</span>;
                    }}
                    sx={{
                      '& .MuiSelect-select': {
                        fontSize: '0.875rem'
                      }
                    }}
                  >
                    {userTypeOptions.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                            {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>

          {/* Linked Accounts */}
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              Linked Accounts
            </Typography>
            
            {/* Existing Linked Accounts */}
            {formData.linkedAccounts.map((account) => {
              const existingRoleHolder = getExistingRoleHolder(account, account.userType);
              
              return (
              <Grid container columnSpacing={2} key={account.id} sx={{ mb: 2 }}>
                <Grid size={6}>
                  <TextField
                    label="Account Name"
                    value={account.accountName}
                    disabled
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid size={6}>
                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                      <Box sx={{ flex: 1 }}>
                    <FormControl fullWidth variant="outlined" size="small">
                          <InputLabel>User Category</InputLabel>
                      <Select
                            value={account.userType === INCOMPLETE_ROLE_LABEL ? "" : account.userType}
                            label="User Category"
                            displayEmpty
                            renderValue={(selected) => {
                              if (!selected) {
                                return (
                                  <span style={{ color: '#9ca3af', fontStyle: 'italic', fontSize: '0.875rem' }}>
                                    {INCOMPLETE_ROLE_LABEL}
                                  </span>
                                );
                              }
                              return <span style={{ fontSize: '0.875rem' }}>{selected}</span>;
                            }}
                            disabled={allowedAccountRoles.length === 0}
                            onChange={(event) =>
                              handleLinkedAccountRoleChange(account.id, event.target.value)
                            }
                            MenuProps={{ sx: { zIndex: 99999 } }}
                            sx={{
                              '& .MuiSelect-select': {
                                fontSize: '0.875rem'
                              }
                            }}
                          >
                            <MenuItem value="" disabled>
                              <em>Select a role</em>
                            </MenuItem>
                            {allowedAccountRoles.map((type) => (
                          <MenuItem key={type} value={type}>
                            {type}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                        {existingRoleHolder && (
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              color: '#ef4444', 
                              fontStyle: 'italic',
                              display: 'block',
                              mt: 0.5,
                              fontSize: '0.75rem'
                            }}
                          >
                            Overriding existing {account.userType}: {existingRoleHolder}
                          </Typography>
                        )}
                      </Box>
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveAccount(account.id)}
                        sx={{ color: "#ef4444", mt: 0.5 }}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Box>
                </Grid>
              </Grid>
              );
            })}
            
            {/* Add New Account Row */}
            <Grid container columnSpacing={2} sx={{ mb: 2 }}>
              <Grid size={6}>
                <Autocomplete
                  freeSolo
                  options={customers.filter((customer) => {
                    if (customer.optionType === "noResults") return true;
                    return !formData.linkedAccounts.some(
                      (linkedAccount) => (linkedAccount.customerId || linkedAccount.id) === customer.id
                    );
                  })}
                  value={null}
                  inputValue={newAccount.accountName}
                  loading={loadingCustomers}
                  getOptionLabel={(option) => {
                    if (typeof option === 'string') return option;
                    if (option.optionType === 'noResults') return option.searchTerm || '';
                    return option.name || '';
                  }}
                  isOptionEqualToValue={(option, value) => {
                    if (!option) return false;
                    if (typeof value === 'string') {
                      return option.name === value;
                    }
                    if (value && value.id) {
                    return option.id === value.id;
                    }
                    return false;
                  }}
                onInputChange={(event, newValue, reason) => {
                  if (reason === 'input') {
                      setNewAccount((prev) => ({ 
                        ...prev, 
                        accountName: newValue || '', 
                        customerId: null, 
                        bookkeeper: null, 
                        manager: null 
                      }));
                    if (newValue && newValue.length >= 2) {
                      setLinkedAccountsOpen(true);
                      fetchCustomersList(newValue);
                    } else {
                      setCustomers([]);
                      setLinkedAccountsOpen(false);
                    }
                  } else if (reason === 'clear') {
                      setNewAccount((prev) => ({ 
                        ...prev, 
                        accountName: '', 
                        customerId: null, 
                        bookkeeper: null, 
                        manager: null 
                      }));
                    setCustomers([]);
                    setLinkedAccountsOpen(false);
                  }
                }}
                onChange={(event, newValue, reason) => {
                  if (newValue && typeof newValue === 'object') {
                    if (newValue.optionType === 'noResults') {
                      handleCreateNewAccount();
                      setCustomers([]);
                        setNewAccount((prev) => ({ 
                          ...prev, 
                          accountName: '', 
                          customerId: null, 
                          bookkeeper: null, 
                          manager: null 
                        }));
                      setLinkedAccountsOpen(false);
                      return;
                    }
                      setNewAccount((prev) => ({
                        ...prev,
                        accountName: newValue.name,
                        customerId: newValue.id,
                        bookkeeper: newValue.bookkeeper || null,
                        manager: newValue.manager || null,
                      }));
                    setCustomers([]);
                    setLinkedAccountsOpen(false);
                  } else if (newValue === null || newValue === '') {
                      setNewAccount((prev) => ({ 
                        ...prev, 
                        accountName: '', 
                        customerId: null, 
                        bookkeeper: null, 
                        manager: null 
                      }));
                    setCustomers([]);
                    setLinkedAccountsOpen(false);
                  } else if (typeof newValue === 'string') {
                      setNewAccount((prev) => ({ 
                        ...prev, 
                        accountName: newValue, 
                        customerId: null, 
                        bookkeeper: null, 
                        manager: null 
                      }));
                  }
                }}
                filterOptions={(options) => options}
                open={linkedAccountsOpen}
                onOpen={() => {
                  if (newAccount.accountName && newAccount.accountName.length >= 2) {
                    setLinkedAccountsOpen(true);
                  }
                }}
                onClose={() => {
                    // prevent MUI automatic close; state drives visibility
                }}
                selectOnFocus
                clearOnBlur={false}
                handleHomeEndKeys
                onBlur={() => {
                  setCustomers([]);
                  setLinkedAccountsOpen(false);
                }}
                loadingText="Searching accounts..."
                noOptionsText="Type at least 2 characters to search accounts..."
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Account Name"
                    size="small"
                    fullWidth
                  />
                )}
                  renderOption={(props, option) => {
                    if (option.optionType === 'noResults') {
                      return (
                        <li {...props} key={option.id}>
                          <Box sx={{ width: '100%' }}>
                            <Typography variant="body2" sx={{ px: 2, py: 1 }}>
                              No existing account matches "<em>{option.searchTerm}</em>"
                            </Typography>
                            <Divider />
                            <Box
                              sx={{ px: 2, py: 1, cursor: 'pointer' }}
                              onMouseDown={(event) => event.preventDefault()}
                              onClick={() => {
                                handleCreateNewAccount();
                                setCustomers([]);
                              }}
                            >
                              <Typography variant="body2" sx={{ color: '#627CFB', fontWeight: 500 }}>
                                + Create New Account
                              </Typography>
                            </Box>
                          </Box>
                        </li>
                      );
                    }

                    const { key, ...otherProps } = props;
                    return (
                      <li key={option.id} {...otherProps}>
                        <div>
                          <div style={{ fontWeight: 'bold', fontSize: '0.875rem' }}>
                            {option.name}
                          </div>
                          <div style={{ fontSize: '0.75rem', color: '#666' }}>
                            {option.primary_contact && `Contact: ${option.primary_contact}`}
                            {option.email && ` • ${option.email}`}
                          </div>
                        </div>
                      </li>
                    );
                  }}
                  slotProps={{
                    popper: {
                      placement: 'bottom-start',
                      style: { 
                        zIndex: 999999,
                        position: 'fixed'
                      },
                      modifiers: [
                        {
                          name: 'flip',
                          enabled: false,
                        },
                        {
                          name: 'preventOverflow',
                          enabled: false,
                        },
                      ],
                    },
                  }}
                />
              </Grid>
              <Grid size={6}>
                <Box>
                <FormControl fullWidth size="small">
                    <InputLabel>User Category</InputLabel>
                  <Select
                    value={newAccount.userType}
                    onChange={(event) => {
                      setNewAccount(prev => ({ ...prev, userType: event.target.value }));
                    }}
                      label="User Category"
                      disabled={allowedAccountRoles.length === 0}
                    MenuProps={{ sx: { zIndex: 99999 } }}
                      renderValue={(selected) => {
                        return <span style={{ fontSize: '0.875rem' }}>{selected || ''}</span>;
                      }}
                      sx={{
                        '& .MuiSelect-select': {
                          fontSize: '0.875rem'
                        }
                      }}
                    >
                      <MenuItem value="">
                        <em>Select a role</em>
                      </MenuItem>
                      {allowedAccountRoles.map((type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                  
                  {/* Warning message for existing role holder */}
                  {newAccount.userType && newAccount.customerId && (() => {
                    const existingRoleHolder = getExistingRoleHolder(newAccount, newAccount.userType);
                    if (existingRoleHolder) {
                      return (
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            color: '#ef4444', 
                            fontStyle: 'italic',
                            display: 'block',
                            mt: 0.5,
                            fontSize: '0.75rem'
                          }}
                        >
                          Overriding existing {newAccount.userType}: {existingRoleHolder}
                        </Typography>
                      );
                    }
                    return null;
                  })()}
                </Box>
              </Grid>
            </Grid>

            {/* Add Account Button */}
            <Button
              startIcon={<Add />}
              onClick={handleAddAccount}
              disabled={
                allowedAccountRoles.length === 0 ||
                !newAccount.customerId ||
                !newAccount.userType
              }
              sx={{
                color: "#627CFB !important",
                textTransform: "none",
                fontWeight: 500,
                border: (newAccount.customerId && newAccount.userType && allowedAccountRoles.length > 0)
                  ? "1.5px solid #627CFB"
                  : "1.5px solid transparent",
                opacity: allowedAccountRoles.length === 0 ? 0.5 : 1,
                transition: "all 0.2s ease-in-out",
                "& .MuiButton-startIcon": {
                  color: "#627CFB !important",
                },
                "&:hover": {
                  backgroundColor: allowedAccountRoles.length === 0
                    ? "transparent"
                    : "rgba(98, 124, 251, 0.04)",
                  border: (newAccount.customerId && newAccount.userType && allowedAccountRoles.length > 0)
                    ? "1.5px solid #627CFB"
                    : "1.5px solid transparent",
                },
              }}
            >
              Add Account
            </Button>
            {formData.userType && allowedAccountRoles.length === 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                Internal users must be mapped from the Account Management page.
              </Typography>
            )}
          </Box>
        </Stack>
      </DialogContent>
      
      <Divider />
      
      <DialogActions sx={{ p: 3, gap: 2 }}>
        <Button 
          onClick={onClose} 
          sx={{ color: "#627CFB" }}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained"
          disabled={!isFormValid}
          sx={{ 
            backgroundColor: "#627CFB",
            "&:hover": {
              backgroundColor: "#4f46e5"
            }
          }}
        >
          Create User
        </Button>
      </DialogActions>
    </Dialog>
  );
}
